import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useToast } from './contexts/ToastContext';
import { useJWTAuth } from './auth/JWTAuthContext';
import AuthGuard from './components/AuthGuard';
import { Transaction as SolanaTransaction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { useCurrentAccount, useSignAndExecuteTransaction, ConnectButton } from '@mysten/dapp-kit';
import { Transaction as SuiTransaction } from '@mysten/sui/transactions';

const Shop = () => {
  const [searchParams] = useSearchParams();
  const { publicKey, connected } = useWallet();
  const { getAuthHeaders, isAuthenticated, user: jwtUser } = useJWTAuth();
  const { warning, success: showSuccess, error: showError, info } = useToast();
  const suiAccount = useCurrentAccount();
  const { mutateAsync: signAndExecuteTransaction } = useSignAndExecuteTransaction();
  
  const [themes, setThemes] = useState([]);
  const [shopItems, setShopItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [balances, setBalances] = useState({});
  const [tokenMeta, setTokenMeta] = useState({});
  const [depositing, setDepositing] = useState(null);
  const [depositAmountInputs, setDepositAmountInputs] = useState({});
  const [purchasing, setPurchasing] = useState({});
  const [itemQuantities, setItemQuantities] = useState({});

  // Determine which sections are initially expanded based on URL params
  const initialSection = searchParams.get('section') || null;
  const [expandedSections, setExpandedSections] = useState({
    shopItems: true, // Default to expanded
    themes: initialSection === 'themes',
    potions: initialSection === 'potions',
    players: initialSection === 'players',
    battlefields: initialSection === 'battlefields',
    bosses: initialSection === 'bosses',
  });

  // Load balance and token metadata
  const loadBalance = useCallback(async () => {
    if (!isAuthenticated() || !jwtUser?.id) return;

    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const [balanceRes, tokensRes] = await Promise.all([
        fetch('/api/ledger/balance', { headers }),
        fetch('/api/ledger/tokens', { headers }).catch(() => null)
      ]);

      const balanceJson = await balanceRes.json();
      setBalances(balanceJson.data || {});

      if (tokensRes && tokensRes.ok) {
        const tokensJson = await tokensRes.json();
        const metaMap = {};
        if (tokensJson?.data) {
          for (const token of tokensJson.data) {
            if (token.token_symbol) {
              metaMap[`${token.chain}:${token.token_symbol}`] = token;
            }
            if (token.token_address) {
              metaMap[`${token.chain}:${token.token_address}`] = token;
            }
          }
        }
        setTokenMeta(metaMap);
      }
    } catch (e) {
      console.error('Failed loading balance', e);
    }
  }, [isAuthenticated, getAuthHeaders, jwtUser?.id]);

  // Load shop items from API
  const loadShopItems = useCallback(async () => {
    try {
      const response = await fetch('/api/shop/manifest?shopType=website&themeId=soulwars-default&tokenSymbol=soulwars', {
        headers: {
          'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load shop items');
      }

      const data = await response.json();
      console.log('Shop items data:', data);
      if (data.success && data.data?.items) {
        setShopItems(data.data.items);
        // Initialize quantities to 1 for each item
        const initialQuantities = {};
        data.data.items.forEach(item => {
          initialQuantities[item.shopItemId] = 1;
        });
        setItemQuantities(initialQuantities);
      }
    } catch (err) {
      setError('Failed to load shop items: ' + err.message);
    }
  }, []);

  // Load themes from API (keep for themes section)
  useEffect(() => {
    const loadThemes = async () => {
      try {
        const response = await fetch('/api/themes', {
          headers: {
            'x-api-key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load themes');
        }

        const data = await response.json();
        setThemes(data);
      } catch (err) {
        console.error('Failed to load themes:', err);
      }
    };

    loadThemes();
  }, []);

  // Load shop items and balance
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadShopItems(), loadBalance()]);
      setLoading(false);
    };
    loadData();
  }, [loadShopItems, loadBalance]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Deposit helpers (from Account.jsx)
  const getSolanaProvider = () => {
    const provider = (window?.phantom && window?.phantom?.solana) || null;
    if (provider?.isPhantom) {
      return provider;
    }
    if (window?.solana?.isPhantom) {
      return window.solana;
    }
    return null;
  };

  const decodeBase64ToUint8Array = (base64) => {
    if (typeof base64 !== 'string') return new Uint8Array();
    if (typeof window !== 'undefined' && window.atob) {
      const binary = window.atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    }
    return Uint8Array.from(Buffer.from(base64, 'base64'));
  };

  const computeDepositValues = (inputValue, decimals, mode) => {
    if (!inputValue || !Number.isFinite(decimals)) {
      return { tokens: null, raw: null };
    }
    const numeric = Number(inputValue);
    if (!Number.isFinite(numeric)) {
      return { tokens: null, raw: null };
    }
    if (mode === 'raw') {
      const raw = Math.round(numeric);
      const tokens = raw / Math.pow(10, decimals);
      return { tokens, raw };
    }
    const tokens = numeric;
    const raw = Math.round(tokens * Math.pow(10, decimals));
    return { tokens, raw };
  };

  const handleDeposit = async (chain, tokenSymbol, amountInput) => {
    const key = `${chain}:${tokenSymbol}`;
    setDepositing(key);

    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const metadataKey = `${chain}:${tokenSymbol}`;
      const meta = tokenMeta[metadataKey] || {};
      const tokenDecimals = typeof meta.decimals === 'number' ? meta.decimals : null;
      const mode = 'tokens';
      const { tokens, raw } = tokenDecimals != null ? computeDepositValues(amountInput, tokenDecimals, mode) : { tokens: null, raw: null };
      if (tokenDecimals != null && (tokens == null || raw == null)) {
        throw new Error('Invalid deposit amount');
      }

      const response = await fetch('/api/ledger/deposit/build', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chain,
          tokenSymbol,
          amount: mode === 'raw' ? tokens ?? 0 : amountInput,
          rawAmount: tokenDecimals != null ? raw : null
        })
      });

      const buildData = await response.json();

      if (!response.ok || !buildData.success) {
        throw new Error(buildData.message || buildData.error || 'Failed to build deposit transaction');
      }

      const txData = buildData.data;
      const solanaProvider = getSolanaProvider();
      if (!solanaProvider) {
        throw new Error('Phantom wallet not found. Please install Phantom.');
      }

      if (!solanaProvider.isConnected) {
        await solanaProvider.connect();
      }

      if (chain === 'solana') {
        const txBytes = Array.isArray(txData.transaction)
          ? Uint8Array.from(txData.transaction)
          : decodeBase64ToUint8Array(txData.transaction);
        const transaction = SolanaTransaction.from(Buffer.from(txBytes));

        if (!transaction.feePayer) {
          transaction.feePayer = solanaProvider.publicKey || solanaProvider?.publicKey;
        }

        const { signature } = await solanaProvider.signAndSendTransaction(transaction);
        showSuccess(`Deposit transaction sent! Signature: ${signature}`);
        info('Transaction is being processed. Your balance will update once confirmed.');
      } else if (chain === 'sui') {
        if (!suiAccount?.address) {
          throw new Error('Connect a Sui wallet via the Account Linking modal before depositing.');
        }

        const transactionBytes = Array.isArray(txData.transaction)
          ? new Uint8Array(txData.transaction)
          : decodeBase64ToUint8Array(txData.transaction);

        const suiTransaction = SuiTransaction.from(transactionBytes);

        const result = await signAndExecuteTransaction({
          transaction: suiTransaction,
          options: {
            showEffects: true,
            showEvents: true,
            showBalanceChanges: true
          }
        });

        const digest = result?.digest || result?.effects?.transactionDigest || 'pending';
        showSuccess(`Deposit transaction sent! Digest: ${digest}`);
        info('Transaction is being processed. Your balance will update once confirmed.');
      }

      setTimeout(() => {
        loadBalance();
      }, 3000);
    } catch (error) {
      console.error('Deposit error:', error);
      const message = error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Deposit failed';
      showError(message);
    } finally {
      setDepositing(null);
    }
  };

  const handleMockPurchase = (itemType, itemId, itemName) => {
    if (!connected) {
      warning('Please connect your Phantom wallet to make a purchase.');
      return;
    }

    warning(
      `Purchase functionality coming soon!\n\n` +
      `Item: ${itemName}\n` +
      `Type: ${itemType}\n` +
      `Price: $19.95\n` +
      `Wallet: ${publicKey.toString().slice(0, 8)}...${publicKey.toString().slice(-8)}`
    );
  };

  const handlePurchase = async (shopItem) => {
    if (!isAuthenticated()) {
      warning('Please log in to make a purchase.');
      return;
    }

    const quantity = itemQuantities[shopItem.shopItemId] || 1;
    if (quantity < 1) {
      warning('Please select a quantity of at least 1.');
      return;
    }

    const key = shopItem.shopItemId;
    setPurchasing(prev => ({ ...prev, [key]: true }));

    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch('/api/shop/purchase', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          shopItemId: shopItem.shopItemId,
          quantity: quantity,
          tokenSymbol: 'soulwars',
          themeId: 'soulwars-default'
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Purchase failed');
      }

      showSuccess(`Successfully purchased ${quantity}x ${shopItem.displayName || shopItem.name}! ${shopItem.quantity * quantity} items added to your player vault.`);
      await loadBalance();
    } catch (error) {
      console.error('Purchase error:', error);
      const message = error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Purchase failed';
      showError(message);
    } finally {
      setPurchasing(prev => ({ ...prev, [key]: false }));
    }
  };

  // Helper to get asset URL from S3
  const getAssetUrl = (themeId, assetType, filename) => {
    const bucket = 'soulwars';
    return `https://${bucket}.s3.amazonaws.com/themes/${themeId}/${filename}`;
  };

  // Helper to get preview image for a theme (prefer cover image)
  const getThemePreviewImage = (theme) => {
    // Use cover image if available
    if (theme.coverImage) {
      return getAssetUrl(theme.id, 'cover', theme.coverImage);
    }
    // Fallback to other assets
    if (theme.assets?.battlefields?.length > 0) {
      return getAssetUrl(theme.id, 'battlefields', theme.assets.battlefields[0]);
    }
    if (theme.assets?.players?.length > 0) {
      return getAssetUrl(theme.id, 'players', theme.assets.players[0]);
    }
    if (theme.assets?.screens?.length > 0) {
      return getAssetUrl(theme.id, 'screens', theme.assets.screens[0]);
    }
    return null;
  };

  // Mock data for other shop sections
  const potions = [
    { id: 'potion-health-mega', name: 'Mega Health Potion', description: 'Restores 500 HP instantly', price: 19.95, image: '/assets/items/equip_potion_red_4.png' },
    { id: 'potion-mana-mega', name: 'Mega Mana Potion', description: 'Restores 500 MP instantly', price: 19.95, image: '/assets/items/equip_potion_blue_4.png' },
    { id: 'potion-stamina-mega', name: 'Mega Stamina Potion', description: 'Boosts stamina regeneration by 50%', price: 19.95, image: '/assets/items/equip_potion_green_4.png' },
    { id: 'potion-legendary', name: 'Legendary Elixir', description: 'Temporary invincibility for 10 seconds', price: 19.95, image: '/assets/items/equip_potion_purple_4.png' },
  ];

  const playerCustomizations = [
    { id: 'player-custom-1', name: 'Elite Warrior Skin', description: 'Exclusive elite hero appearance', price: 19.95, image: '/assets/players/elite1.png' },
    { id: 'player-custom-2', name: 'Shadow Knight Skin', description: 'Dark and mysterious knight appearance', price: 19.95, image: '/assets/players/elite5.png' },
    { id: 'player-custom-3', name: 'Divine Paladin Skin', description: 'Holy hero with golden armor', price: 19.95, image: '/assets/players/elite7.png' },
    { id: 'player-custom-4', name: 'Dragon Slayer Skin', description: 'Legendary dragon-scale armor', price: 19.95, image: '/assets/players/elite9.png' },
  ];

  const battlefieldPacks = [
    { id: 'battlefield-1', name: 'Ancient Dungeon Pack', description: '5 unique dungeon battlefields', price: 19.95, image: '/assets/battlefields/dungeon1.jpg' },
    { id: 'battlefield-2', name: 'Dark Caverns Pack', description: '5 atmospheric cave battlefields', price: 19.95, image: '/assets/battlefields/dungeon2.jpg' },
    { id: 'battlefield-3', name: 'Mystical Realms Pack', description: '5 magical realm battlefields', price: 19.95, image: '/assets/battlefields/dungeon3.jpg' },
  ];

  const bossPacks = [
    { id: 'boss-1', name: 'Legendary Boss Pack', description: '3 epic legendary boss encounters', price: 19.95 },
    { id: 'boss-2', name: 'Mythical Creatures Pack', description: '3 mythical creature bosses', price: 19.95 },
    { id: 'boss-3', name: 'Undead Lords Pack', description: '3 powerful undead boss fights', price: 19.95 },
  ];

  // Helper function to format battlefield image URLs
  const formatBattlefieldImage = (imagePath, themeId = null) => {
    if (!imagePath) return null;
    
    // If already a full URL, return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Check if it's an S3 path (for custom themes)
    const DEFAULT_THEME_ID = process.env.REACT_APP_DEFAULT_THEME_ID || 'soulwars-default';
    const awsRegion = process.env.REACT_APP_AWS_APP_REGION || 'us-east-1';
    const resolvedTheme = themeId || DEFAULT_THEME_ID;
    
    // If theme is not default, try S3 path
    if (resolvedTheme !== DEFAULT_THEME_ID) {
      const imagePathFormatted = imagePath.startsWith('battlefields/')
        ? imagePath
        : `battlefields/${imagePath}`;
      return `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${resolvedTheme}/${imagePathFormatted}`;
    }
    
    // Default theme - use local assets
    return imagePath.startsWith('battlefields/')
      ? `/assets/${imagePath}`
      : `/assets/battlefields/${imagePath}`;
  };

  // Battlefields Section Component
  const BattlefieldsSection = () => {
    const [battlefields, setBattlefields] = useState([]);
    const [loading, setLoading] = useState(true);
    const { getAuthHeaders, isAuthenticated } = useJWTAuth();
    const { warning, success } = useToast();

    useEffect(() => {
      const loadBattlefields = async () => {
        try {
          setLoading(true);
          const response = await fetch('/api/battlefields/for-sale', {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to load battlefields');
          }

          const data = await response.json();
          if (data.success) {
            setBattlefields(data.data || []);
          }
        } catch (error) {
          console.error('Error loading battlefields:', error);
        } finally {
          setLoading(false);
        }
      };

      loadBattlefields();
    }, []);

    const handlePurchase = async (battlefieldInstanceId, battlefieldName, price) => {
      if (!connected) {
        warning('Please connect your Phantom wallet to make a purchase.');
        return;
      }

      if (!isAuthenticated()) {
        warning('Please log in to purchase a battlefield.');
        return;
      }

      try {
        const response = await fetch(`/api/battlefields/${battlefieldInstanceId}/purchase`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
            'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
          },
          body: JSON.stringify({})
        });

        const data = await response.json();

        if (data.success) {
          success(`Successfully purchased ${battlefieldName}!`);
          // Remove from list
          setBattlefields(prev => prev.filter(bf => bf.id !== battlefieldInstanceId));
          // Redirect to battlefield management page
          window.location.href = '/battlefields';
        } else {
          warning(`Purchase failed: ${data.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error purchasing battlefield:', error);
        warning('Error purchasing battlefield. Please try again.');
      }
    };

    if (loading) {
      return <p className="text-gray-500 text-center py-8">Loading battlefields...</p>;
    }

    if (battlefields.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">No battlefields available for purchase at this time.</p>
          <p className="text-gray-400 text-sm">Check back soon for new battlefield opportunities!</p>
        </div>
      );
    }

    return (
      <div>
        <p className="text-gray-600 mb-6">
          Purchase your own battlefield instance and collect loot from battles. Each battlefield has unique Loot Team efficiency stats.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {battlefields.map((bf) => {
            const themeBf = bf.themeBattlefield || {};
            const price = bf.priceSoulwars || 1000000;
            
            return (
              <div key={bf.id} className="bg-gray-50 rounded-lg shadow p-4 flex flex-col">
                {themeBf.image && (
                  <div className="w-full mb-4 bg-gray-200 rounded overflow-hidden flex items-center justify-center h-32">
                    <img
                      src={formatBattlefieldImage(themeBf.image, themeBf.theme)}
                      alt={themeBf.name}
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <h3 className="text-lg font-bold mb-2 text-gray-800">{themeBf.name || 'Unknown Battlefield'}</h3>
                <p className="text-sm text-gray-600 mb-4 flex-grow">{themeBf.description || 'A mysterious battlefield awaits...'}</p>
                
                {/* Loot Team Stats */}
                <div className="mb-4 text-xs text-gray-500">
                  <div className="grid grid-cols-2 gap-2">
                    <div>💰 Tokens: {Math.round(bf.lootTeamTokenEfficiency || 0)}%</div>
                    <div>📦 Items: {Math.round(bf.lootTeamItemEfficiency || 0)}%</div>
                    <div>🧪 Potions: {Math.round(bf.lootTeamPotionEfficiency || 0)}%</div>
                    <div>💎 Gems: {Math.round(bf.lootTeamGemEfficiency || 0)}%</div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-green-600">{price.toLocaleString()} $SOULWARS</span>
                  <button
                    onClick={() => handlePurchase(bf.id, themeBf.name, price)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const ShopItem = ({ item, itemType, onPurchase, isTheme }) => (
    <div className="bg-gray-50 rounded-lg shadow p-4 flex flex-col">
      {item.image && (
        <div 
          className={`w-full mb-4 bg-gray-200 rounded overflow-hidden flex items-center justify-center ${
            isTheme ? 'h-40' : 'h-48'
          }`}
          style={isTheme ? { aspectRatio: '2/1' } : {}}
        >
          <img
            src={item.image}
            alt={item.name}
            className={`max-w-full max-h-full ${isTheme ? 'object-cover w-full h-full' : 'object-contain'}`}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      <h3 className="text-lg font-bold mb-2 text-gray-800">{item.name}</h3>
      <p className="text-sm text-gray-600 mb-4 flex-grow">{item.description}</p>
      <div className="flex justify-between items-center">
        <span className="text-2xl font-bold text-green-600">${item.price.toFixed(2)}</span>
        <button
          onClick={() => onPurchase(itemType, item.id, item.name)}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
        >
          Buy Now
        </button>
      </div>
    </div>
  );

  const CollapsibleSection = ({ title, isExpanded, onToggle, children, hidden = false }) => {
    if (hidden) {
      return null;
    }
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
        <button
          onClick={onToggle}
          className="w-full px-6 py-4 flex justify-between items-center bg-gradient-to-r from-gray-800 to-gray-700 text-white hover:from-gray-700 hover:to-gray-600 transition-colors"
        >
          <h2 className="text-2xl font-bold">{title}</h2>
          <span className="text-3xl transform transition-transform" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
          </span>
        </button>
        {isExpanded && (
          <div className="p-6">
            {children}
          </div>
        )}
      </div>
    );
  };

  const formatAmount = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(val);
  };

  const getBalance = () => {
    // Get SOULWARS balance from solana chain
    const solanaBalances = balances['solana'] || {};
    //Return in integer format
    return Math.floor(Number(solanaBalances['soulwars'] || solanaBalances['SOULWARS'] || 0));
  };

  const getTokenMeta = () => {
    return tokenMeta['solana:soulwars'] || tokenMeta['solana:SOULWARS'] || {};
  };

  const ShopItemCard = ({ item }) => {
    console.log('Shop item:', item);
    const quantity = itemQuantities[item.shopItemId] || 1;
    const totalCostUsd = item.priceUsd * quantity;
    const totalCostTokens = item.tokenPrice.tokenAmount * quantity;
    const totalItemsReceived = item.quantity * quantity;
    const isPurchasing = purchasing[item.shopItemId] || false;
    const currentBalance = getBalance();
    const canAfford = currentBalance >= totalCostTokens;

    const getItemImage = () => {

      let imageUrl = null;  
      if (item.iconKey) {
        imageUrl = `https://soulwars.s3.amazonaws.com/themes/${item.themeId}/items/${item.iconKey}.png`;
      }
      console.log('Image URL:', imageUrl);
      return imageUrl;
    };

    return (
      <div className="bg-gray-50 rounded-lg shadow p-4 flex flex-col">
        {getItemImage() && (
          <div className="w-full mb-4 bg-gray-200 rounded overflow-hidden flex items-center justify-center h-48">
            <img
              src={getItemImage()}
              alt={item.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        <h3 className="text-lg font-bold mb-2 text-gray-800">{item.displayName || item.name}</h3>
        {item.badge && (
          <span className="inline-block bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded mb-2">
            {item.badge}
          </span>
        )}
        <p className="text-sm text-gray-600 mb-2 flex-grow">
          {item.quantity > 1 && (
            <span className="font-semibold text-blue-600">Get {item.quantity} per purchase</span>
          )}
        </p>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Quantity:</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setItemQuantities(prev => ({
                ...prev,
                [item.shopItemId]: Math.max(1, (prev[item.shopItemId] || 1) - 1)
              }))}
              className="px-3 py-1 bg-gray-300 hover:bg-gray-400 rounded text-gray-800 font-bold"
              disabled={isPurchasing}
            >
              -
            </button>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => {
                const val = Math.max(1, parseInt(e.target.value) || 1);
                setItemQuantities(prev => ({ ...prev, [item.shopItemId]: val }));
              }}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
              disabled={isPurchasing}
            />
            <button
              onClick={() => setItemQuantities(prev => ({
                ...prev,
                [item.shopItemId]: (prev[item.shopItemId] || 1) + 1
              }))}
              className="px-3 py-1 bg-gray-300 hover:bg-gray-400 rounded text-gray-800 font-bold"
              disabled={isPurchasing}
            >
              +
            </button>
          </div>
          {quantity > 1 && (
            <p className="text-xs text-gray-500 mt-1">
              You'll receive {totalItemsReceived} items total
            </p>
          )}
        </div>
        <div className="mb-4">
          <div className="text-sm text-gray-600">
            <div>Price: {formatAmount(totalCostTokens)} {item.tokenPrice.tokenSymbol.toUpperCase()}</div>
            <div className="text-xs text-gray-500">${totalCostUsd.toFixed(2)} USD</div>
          </div>
        </div>
        <button
          onClick={() => handlePurchase(item)}
          disabled={isPurchasing || !canAfford}
          className={`px-6 py-2 rounded-md transition-colors font-medium ${
            canAfford && !isPurchasing
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
          }`}
        >
          {isPurchasing ? 'Processing...' : canAfford ? 'Buy Now' : 'Insufficient Balance'}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading shop...</div>
      </div>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-4">Shop</h1>
        </div>

        {/* Balance and Deposit Section */}
        {isAuthenticated() && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 mb-6 border border-white border-opacity-20">
            <div className="flex items-center gap-4 mb-4">
              <h2 className="text-2xl font-bold text-white">Your Balance:</h2>
              <div className="text-3xl font-bold text-yellow-400">
                {formatAmount(getBalance())}{' '}
                <a
                  href="https://pump.fun/coin/HXik3MQbaoKdFEdbirN8xS5PmjyaQQz66TgZ9Ygvpump"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:text-yellow-300 underline cursor-pointer"
                >
                  $SOULWARS
                </a>
              </div>
            </div>
            <div className="border-t border-white border-opacity-20 pt-4">
              <div className="text-sm text-gray-300 mb-2">Deposit SOULWARS Tokens to add to your balance. You can deposit or withdraw at any time.</div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={depositAmountInputs['solana:soulwars'] || ''}
                  onChange={(e) => setDepositAmountInputs(prev => ({
                    ...prev,
                    'solana:soulwars': e.target.value
                  }))}
                  placeholder="Amount to deposit"
                  className="flex-1 px-3 py-2 bg-gray-800 bg-opacity-50 text-white rounded border border-gray-700 rounded"
                  min="0"
                  step="any"
                />
                <button
                  onClick={() => {
                    const amt = parseFloat(depositAmountInputs['solana:soulwars']);
                    if (Number.isFinite(amt) && amt > 0) {
                      handleDeposit('solana', 'soulwars', amt);
                    }
                  }}
                  disabled={depositing === 'solana:soulwars' || !depositAmountInputs['solana:soulwars'] || parseFloat(depositAmountInputs['solana:soulwars']) <= 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded font-medium"
                >
                  {depositing === 'solana:soulwars' ? 'Processing...' : 'Deposit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Shop Items Section */}
        <CollapsibleSection
          title="Game Items"
          isExpanded={expandedSections.shopItems !== false}
          onToggle={() => toggleSection('shopItems')}
        >
          {shopItems.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No shop items available. Check back soon!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {shopItems.map(item => (
                <ShopItemCard key={item.shopItemId} item={item} />
              ))}
            </div>
          )}
        </CollapsibleSection>

        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Themes Section */}
        <CollapsibleSection
          title="Themes"
          isExpanded={expandedSections.themes}
          onToggle={() => toggleSection('themes')}
          hidden={true}
        >
          <p className="text-gray-600 mb-6">
            Complete themed experiences including custom maps, player images, music, and intermission screens.
          </p>
          {themes.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No themes available yet. Check back soon!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {themes.map(theme => (
                <ShopItem
                  key={theme.id}
                  item={{
                    id: theme.id,
                    name: theme.name,
                    description: theme.description || 'Custom theme with unique assets and experiences',
                    price: 19.95,
                    image: getThemePreviewImage(theme)
                  }}
                  itemType="theme"
                  onPurchase={handleMockPurchase}
                  isTheme={true}
                />
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Potions Section */}
        <CollapsibleSection
          title="Potions"
          isExpanded={expandedSections.potions}
          onToggle={() => toggleSection('potions')}
          hidden={true}
        >
          <p className="text-gray-600 mb-6">
            Powerful potions to give you an edge in battle. Effects are permanent and stackable.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {potions.map(potion => (
              <ShopItem
                key={potion.id}
                item={potion}
                itemType="potion"
                onPurchase={handleMockPurchase}
              />
            ))}
          </div>
        </CollapsibleSection>

        {/* Player Customizations Section */}
        <CollapsibleSection
          title="Player Customizations"
          isExpanded={expandedSections.players}
          onToggle={() => toggleSection('players')}
          hidden={true}
        >
          <p className="text-gray-600 mb-6">
            Exclusive player skins to stand out on the battlefield. Show your style and intimidate your enemies.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {playerCustomizations.map(customization => (
              <ShopItem
                key={customization.id}
                item={customization}
                itemType="player-customization"
                onPurchase={handleMockPurchase}
              />
            ))}
          </div>
        </CollapsibleSection>

        {/* Battlefields Section */}
        <CollapsibleSection
          title="Battlefields"
          isExpanded={expandedSections.battlefields}
          onToggle={() => toggleSection('battlefields')}
        >
          <BattlefieldsSection />
        </CollapsibleSection>

        {/* Bosses Section */}
        <CollapsibleSection
          title="Boss Packs"
          isExpanded={expandedSections.bosses}
          onToggle={() => toggleSection('bosses')}
          hidden={true}
        >
          <p className="text-gray-600 mb-6">
            Challenge yourself with exclusive boss encounters. Each pack includes unique mechanics and rewards.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bossPacks.map(pack => (
              <ShopItem
                key={pack.id}
                item={pack}
                itemType="boss-pack"
                onPurchase={handleMockPurchase}
              />
            ))}
          </div>
        </CollapsibleSection>

        {/* Footer */}
        <div className="text-center mt-12 text-gray-300">
          <p className="mb-2">All purchases are one-time payments with lifetime access.</p>
          <p className="text-sm">Need help? Contact support@soulwars.com</p>
        </div>
      </div>
      </div>
    </AuthGuard>
  );
};

export default Shop;

